import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { api } from '@core/api/apiClient';

/**
 * Marketplace menu response from GET /api/get-marketplace-menu?id={menuId}
 */
interface MarketplaceMenuResponse {
  menu: {
    id: number;
    name: string;
    [key: string]: unknown;
  };
}

/**
 * Meitre Network redirect page for `/reserva?exp` and `/book?exp`
 *
 * Fetches the marketplace menu by ID and starts the booking flow
 * with the marketplace context. Replicates the legacy AngularJS
 * `home.reserva` route behavior.
 */
const MeitreNetworkRedirectPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const booking = useBooking();
  const restaurant = useRestaurant();

  useEffect(() => {
    const menuId = searchParams.get('exp');

    if (!menuId) {
      // No marketplace menu — just start normal booking
      navigate('/select-diners', { replace: true });
      return;
    }

    // Fetch marketplace menu and start booking
    api.get<MarketplaceMenuResponse>(`/api/get-marketplace-menu?id=${encodeURIComponent(menuId)}`)
      .then((data) => {
        if (data?.menu) {
          booking.updateBooking({
            specialEvent: null,
            meitreNetworkMenuId: data.menu.id,
          });
          booking.setSelectedMenuFilter([data.menu.id]);
        }
        navigate('/select-diners', { replace: true });
      })
      .catch((err) => {
        console.error('[MeitreNetwork] Failed to load marketplace menu:', err);
        // Fallback: start normal booking
        navigate('/select-diners', { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-brand" />
    </div>
  );
};

export default MeitreNetworkRedirectPage;
